import { jsx as _jsx } from "react/jsx-runtime";
import { Box } from '@mui/material';
import { Droppable, Link } from 'components/custom';
import { getTextColor } from 'utils/color';
import { getLessonUrl } from 'utils/links';
import { DragAndDropOperationNames } from 'utils/drag-and-drop';
import { FIRST_DAY_OF_THE_WEEK, HOURS_PER_DAY, LAST_DAY_OF_THE_WEEK, } from 'utils/date';
import { getDay } from 'date-fns';
const EMPTY_SLOT = 'EMPTY_SLOT';
const SlotEntries = ({ slots, day, hour, rowsUsedAbove, }) => {
    const lessonList = slots.map((slot, index) => {
        if (slot === EMPTY_SLOT) {
            return (_jsx(Box, { sx: {
                    height: '100%',
                    width: `calc(100% / ${slots.length})`,
                } }, index));
        }
        const lessonSlotSx = {
            background: slot.color,
            display: 'grid',
            height: '100%',
            p: '4px',
            placeItems: 'center',
            textDecoration: 'none',
            zIndex: 1,
        };
        const textSx = {
            color: getTextColor(slot.color),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
        };
        /*
         * Cell height generalized to arbitrary `lengthHours`. Grid rows have a 2px
         * gap between them, so covering N rows means `N * 100%` of one row height
         * plus `(N - 1) * 2px` for the gaps. Behaves byte-identically for N = 1
         * (100%) and N = 2 (calc(200% + 2px)) — existing Doppelstunden unchanged.
         * Only merged Untis blocks with N >= 3 benefit from the generalization.
         */
        const { lengthHours: n } = slot;
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- N=1 means default height
        const cellHeight = n > 1 ? `calc(${n * 100}% + ${(n - 1) * 2}px)` : '100%';
        return (_jsx(Droppable, { droppableId: slot.slotId, droppableItemName: slot.name, height: cellHeight, operationName: DragAndDropOperationNames.ASSIGN_OBJECT_TO_LESSON, outlineColor: "white", outlinePosition: "inside", width: `calc(100% / ${slots.length})`, zIndex: 1, children: _jsx(Link, { href: getLessonUrl(slot.slotId), title: slot.name, sx: lessonSlotSx, children: _jsx(Box, { sx: textSx, children: slot.name }) }) }, index));
    });
    return (_jsx(Box, { role: "gridcell", sx: {
            border: 'solid 1px',
            borderColor: 'secondary.light',
            display: 'flex',
            gap: '2px',
            gridColumn: day + 1,
            gridRow: rowsUsedAbove + hour,
            textAlign: 'center',
        }, children: lessonList }));
};
/**
 * Reserves slots `hour+1 .. hour+lengthHours-1` at the same column index by
 * writing EMPTY_SLOT into the corresponding `futureBuffers` entries. Grows
 * `futureBuffers` on demand so each reservation has a home to land in.
 * Returns the touched buffers so the caller can register them in its
 * `connectedSlots` set (used by `fillEmptySlots` to align all hours of
 * the merged block).
 */
const reserveFollowUpSlots = (futureBuffers, index, lengthHours) => {
    const touchedBuffers = [];
    for (let i = 1; i < lengthHours; i++) {
        if (!futureBuffers[i]) {
            futureBuffers[i] = [];
        }
        futureBuffers[i][index] = EMPTY_SLOT;
        touchedBuffers.push(futureBuffers[i]);
    }
    return touchedBuffers;
};
/**
 * Places all lessons of the current hour into `ctx.slots` and reserves
 * follow-up slots for any multi-hour lesson. Returns whether any placed
 * lesson extends the block into future hours (so the caller keeps
 * `connectedSlots` alive).
 *
 * Extracted from the main hour loop purely to keep block-nesting within the
 * linter's `max-depth = 4` budget. Logic is identical to the inlined version.
 */
const placeLessonsForHour = (dataForSlot, ctx) => {
    const { slots, futureBuffers, connectedSlots } = ctx;
    let index = 0;
    let extendsIntoFuture = false;
    for (const d of dataForSlot) {
        while (slots[index] === EMPTY_SLOT) {
            index++;
        }
        slots[index] = d;
        if (d.lengthHours > 1) {
            const reservedBuffers = reserveFollowUpSlots(futureBuffers, index, d.lengthHours);
            reservedBuffers.forEach((buffer) => connectedSlots.add(buffer));
            connectedSlots.add(slots);
            extendsIntoFuture = true;
        }
        index++;
    }
    return { extendsIntoFuture };
};
const fillEmptySlots = (slotSet) => {
    let maxLength = 0;
    slotSet.forEach((s) => {
        maxLength = Math.max(maxLength, s.length);
    });
    slotSet.forEach((slot) => {
        for (let i = 0; i < maxLength; i++) {
            if (!(i in slot)) {
                slot[i] = EMPTY_SLOT;
            }
        }
    });
};
const CourseDashboardCells = ({ data, startDate, onlyOneDay, rowsUsedAbove, }) => {
    const result = [];
    const firstDay = onlyOneDay ? getDay(startDate) : FIRST_DAY_OF_THE_WEEK;
    const lastDay = onlyOneDay ? firstDay : LAST_DAY_OF_THE_WEEK;
    const connectedSlots = new Set();
    for (let day = firstDay; day <= lastDay; day++) {
        /*
         * Sliding buffer of slot-arrays for the upcoming hours of the day.
         * `futureBuffers[0]` is the slot-array for the current hour's SlotEntries.
         * `futureBuffers[i]` (i >= 1) is pre-allocated for `hour + i` and carries
         * the EMPTY_SLOT reservations made by a merged multi-hour block so the
         * follow-up hours don't collide with it.
         *
         * Before generalization the code used a single `nextHourSlots` and a
         * boolean `hasDoubleHour` flag because only Einzel- and Doppelstunden
         * existed (`lengthHours` ∈ {1, 2}). For merged Untis blocks with
         * `lengthHours >= 3` we need to reserve N-1 follow-up hours, and hold
         * the `connectedSlots` set open until ALL reservations are consumed.
         */
        let futureBuffers = [[]];
        for (let hour = 1; hour <= HOURS_PER_DAY; hour++) {
            const dataForSlot = data.filter((c) => c.weekday === day && c.hourInDay === hour);
            const [slots] = futureBuffers;
            /*
             * Tracks whether any open multi-hour reservation extends beyond this hour.
             * If so we keep `connectedSlots` alive so `fillEmptySlots` aligns all
             * hours of the merged block. It is driven by two sources:
             *   1. Carry-over reservations inherited from the previous iteration
             *      (EMPTY_SLOTs already present in `slots`), marking the middle/tail
             *      of an ongoing block.
             *   2. Any lesson with `lengthHours > 1` placed in this hour (tracked
             *      inside `placeLessonsForHour`).
             */
            const hasCarryOver = slots.some((s) => s === EMPTY_SLOT);
            const placeResult = placeLessonsForHour(dataForSlot, {
                connectedSlots,
                futureBuffers,
                slots,
            });
            const extendsIntoFuture = hasCarryOver || placeResult.extendsIntoFuture;
            /*
             * Close a block of connected hours only when no open reservations remain.
             * `futureBuffers[1]` holds the next hour's EMPTY_SLOTs if a block is still
             * ongoing. If it's missing, the block ended at this hour and we can flush.
             */
            if (!extendsIntoFuture) {
                fillEmptySlots(connectedSlots);
                connectedSlots.clear();
            }
            result.push(_jsx(SlotEntries, { slots: slots, day: onlyOneDay ? 1 : day, hour: hour, rowsUsedAbove: rowsUsedAbove }, `${day}-${hour}`));
            /*
             * Advance the sliding buffer: the current hour is done, shift `futureBuffers`
             * left by one so `futureBuffers[0]` is what was `futureBuffers[1]` (or [] if none).
             */
            futureBuffers = futureBuffers.length > 1 ? futureBuffers.slice(1) : [[]];
        }
        /*
         * Day boundary: defensively flush any still-open connected slots so they
         * don't leak into the next day's rendering. Should be empty in practice
         * because no block legitimately spans midnight in a school day.
         */
        if (connectedSlots.size > 0) {
            fillEmptySlots(connectedSlots);
            connectedSlots.clear();
        }
    }
    return result;
};
export default CourseDashboardCells;
